import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Typography,
  Checkbox,
  ListItemText,
  TextField,
  Snackbar,
  Grid,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import SendIcon from '@material-ui/icons/Send';
import Layout from '../components/Layout/Layout';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 300,
  },
  formCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: '#1a1a2e',
  },
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: '100%',
  },
  submitButton: {
    backgroundColor: '#2e7d32',
    color: '#fff',
    padding: theme.spacing(1.5, 4),
    '&:hover': {
      backgroundColor: '#1b5e20',
    },
    '&:disabled': {
      backgroundColor: '#ccc',
    },
  },
  bearishButton: {
    backgroundColor: '#f44336',
    color: '#fff',
    padding: theme.spacing(1.5, 4),
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
    '&:disabled': {
      backgroundColor: '#ccc',
    },
  },
  warningButton: {
    backgroundColor: '#fbc02d',
    color: '#000',
    padding: theme.spacing(1.5, 4),
    '&:hover': {
      backgroundColor: '#f9a825',
    },
    '&:disabled': {
      backgroundColor: '#ccc',
    },
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  selectedUsersBox: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    minHeight: 40,
  },
  summaryCard: {
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    marginTop: theme.spacing(2),
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  summaryLabel: {
    color: '#666',
    fontWeight: 500,
  },
  summaryValue: {
    fontWeight: 600,
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  catalogInfo: {
    padding: theme.spacing(1.5),
    backgroundColor: '#e3f2fd',
    borderRadius: 4,
    marginTop: theme.spacing(1),
  },
  overrideSection: {
    padding: theme.spacing(2),
    backgroundColor: '#fff3e0',
    borderRadius: 4,
    marginTop: theme.spacing(2),
  },
  resultCard: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  successResult: {
    backgroundColor: '#e8f5e9',
    borderLeft: '4px solid #4caf50',
  },
  errorResult: {
    backgroundColor: '#ffebee',
    borderLeft: '4px solid #f44336',
  },
}));

function PlaceOrder() {
  const classes = useStyles();
  const { isAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [catalogs, setCatalogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedCatalog, setSelectedCatalog] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [orderType, setOrderType] = useState('BULLISH ENTRY');
  const [showOverrides, setShowOverrides] = useState(false);
  const [strikeOverride, setStrikeOverride] = useState('');
  const [qtyMultiplier, setQtyMultiplier] = useState('1');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [orderResult, setOrderResult] = useState(null);

  const fetchCatalogs = async () => {
    setLoading(true);
    try {
      const catalogsData = await apiService.getManualOrderCatalogs();
      setCatalogs(catalogsData);
    } catch (error) {
      console.error('Error fetching catalogs:', error);
      setSnackbar({ open: true, message: 'Error loading catalogs', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalogs();
  }, []);

  const handleCatalogChange = async (event) => {
    const catalogId = event.target.value;
    setSelectedCatalog(catalogId);
    setSelectedUsers([]);
    setOrderResult(null);

    if (catalogId) {
      // Get catalog's supported symbols
      const catalog = catalogs.find(c => c.id === catalogId);
      if (catalog && catalog.symbols && catalog.symbols.length > 0) {
        setAvailableSymbols(catalog.symbols);
        setSelectedSymbol(catalog.symbols[0]); // Default to first symbol
      } else {
        setAvailableSymbols(['NIFTY', 'BANKNIFTY']); // Fallback
        setSelectedSymbol('NIFTY');
      }

      try {
        const usersData = await apiService.getManualOrderUsers(catalogId);
        setUsers(usersData);

        // If not admin, auto-select self
        if (!isAdmin() && usersData.length > 0) {
          setSelectedUsers([usersData[0].id]);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setSnackbar({ open: true, message: 'Error loading users for catalog', severity: 'error' });
      }
    } else {
      setUsers([]);
      setAvailableSymbols([]);
      setSelectedSymbol('');
    }
  };

  const handleUserChange = (event) => {
    setSelectedUsers(event.target.value);
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const orderTypeColors = {
    'BULLISH ENTRY': '#4caf50',
    'BEARISH ENTRY': '#f44336',
    'EXIT': '#fbc02d',
  };

  const handleSubmitOrder = async () => {
    if (!selectedCatalog) {
      setSnackbar({ open: true, message: 'Please select a catalog', severity: 'warning' });
      return;
    }

    if (selectedUsers.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one user', severity: 'warning' });
      return;
    }

    const confirmMsg = `You are about to place ${orderType} orders for ${selectedUsers.length} user(s). Continue?`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setSubmitting(true);
    setOrderResult(null);

    try {
      // Get catalog name from selected catalog
      const selectedCatalogObj = catalogs.find(c => c.id === selectedCatalog);
      if (!selectedCatalogObj) {
        setSnackbar({ open: true, message: 'Invalid catalog selected', severity: 'error' });
        return;
      }

      // Map combined order type to backend action
      let action = 'EXIT';
      if (orderType === 'BULLISH ENTRY') {
        action = 'LONG';
      } else if (orderType === 'BEARISH ENTRY') {
        action = 'SHORT';
      }

      const orderData = {
        catalog_name: selectedCatalogObj.name,
        action: action,
        user_ids: isAdmin() ? selectedUsers : undefined,
        symbol: selectedSymbol || 'NIFTY',
        ltp: 0  // Let the system fetch current price
      };

      // Add overrides if enabled
      if (showOverrides) {
        if (strikeOverride) {
          orderData.ltp = parseFloat(strikeOverride);
        }
        if (qtyMultiplier && qtyMultiplier !== '1') {
          orderData.multiplier = parseFloat(qtyMultiplier);
        }
      }

      const result = await apiService.placeManualOrder(orderData);
      setOrderResult(result);
      setSnackbar({
        open: true,
        message: result.message || 'Order placement initiated',
        severity: result.code === 'MO200' ? 'success' : 'warning'
      });
    } catch (error) {
      console.error('Error placing order:', error);
      setOrderResult({ code: 'MO500', message: error.message, data: null });
      setSnackbar({ open: true, message: error.message || 'Error placing order', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getSelectedCatalog = () => {
    return catalogs.find(c => c.id === selectedCatalog);
  };

  const getSelectedUserNames = () => {
    return selectedUsers.map(id => {
      const user = users.find(u => u.id === id);
      return user ? user.name : `User #${id}`;
    });
  };

  if (loading) {
    return (
      <Layout title="Place Order" onRefresh={fetchCatalogs}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const selectedCatalogObj = getSelectedCatalog();

  return (
    <Layout title="Place Order" onRefresh={fetchCatalogs}>
      <Grid container spacing={3}>
        {/* Order Form */}
        <Grid item xs={12} md={8}>
          <Card className={classes.formCard}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Manual Order Placement
            </Typography>

            {/* Catalog Selection */}
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel>Select Catalog</InputLabel>
              <Select
                value={selectedCatalog}
                onChange={handleCatalogChange}
                label="Select Catalog"
              >
                <MenuItem value="">
                  <em>-- Select a Catalog --</em>
                </MenuItem>
                {catalogs.filter(c => c.has_strategies).map((catalog) => (
                  <MenuItem key={catalog.id} value={catalog.id}>
                    {catalog.name} ({catalog.strategy_count} strategies)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Catalog Info */}
            {selectedCatalogObj && (
              <Box className={classes.catalogInfo}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Description:</strong> {selectedCatalogObj.description || 'No description'}
                </Typography>
              </Box>
            )}

            {/* User Selection (Admin only shows multi-select) */}
            {selectedCatalog && users.length > 0 && (
              <>
                <Box mt={2}>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>Select Users</InputLabel>
                    <Select
                      multiple
                      value={selectedUsers}
                      onChange={handleUserChange}
                      label="Select Users"
                      renderValue={(selected) => `${selected.length} user(s) selected`}
                      disabled={!isAdmin()}
                    >
                      {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <Checkbox checked={selectedUsers.indexOf(user.id) > -1} />
                          <ListItemText
                          primary={user.name}
                          secondary={`${user.dp_user_id || 'No DP ID'} - ${user.app_provider || 'N/A'}`}
                        />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {isAdmin() && (
                    <Button size="small" onClick={handleSelectAllUsers}>
                      {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                </Box>

                {/* Selected Users Display */}
                {selectedUsers.length > 0 && (
                  <Box className={classes.selectedUsersBox}>
                    {getSelectedUserNames().map((name, idx) => (
                      <Chip
                        key={idx}
                        label={name}
                        size="small"
                        className={classes.chip}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </>
            )}

            {selectedCatalog && users.length === 0 && (
              <Alert severity="warning" style={{ marginTop: 16 }}>
                No subscribed users found for this catalog. Users must be subscribed to the catalog to place orders.
              </Alert>
            )}

            <Divider className={classes.divider} />

            {/* Symbol Selection */}
            {selectedCatalog && availableSymbols.length > 0 && (
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>Symbol</InputLabel>
                <Select
                  value={selectedSymbol}
                  onChange={(e) => setSelectedSymbol(e.target.value)}
                  label="Symbol"
                >
                  {availableSymbols.map((symbol) => (
                    <MenuItem key={symbol} value={symbol}>
                      {symbol}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Order Type */}
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel>Order Type</InputLabel>
              <Select
                value={orderType}
                onChange={(e) => setOrderType(e.target.value)}
                label="Order Type"
              >
                <MenuItem value="BULLISH ENTRY" style={{ color: orderTypeColors['BULLISH ENTRY'] }}>
                  BULLISH ENTRY
                </MenuItem>
                <MenuItem value="BEARISH ENTRY" style={{ color: orderTypeColors['BEARISH ENTRY'] }}>
                  BEARISH ENTRY
                </MenuItem>
                <MenuItem value="EXIT" style={{ color: orderTypeColors.EXIT }}>
                  EXIT
                </MenuItem>
              </Select>
            </FormControl>

            {/* Override Options */}
            <FormControlLabel
              control={
                <Switch
                  checked={showOverrides}
                  onChange={(e) => setShowOverrides(e.target.checked)}
                  color="primary"
                />
              }
              label="Show Advanced Options"
            />

            {showOverrides && (
              <Box className={classes.overrideSection}>
                <Typography variant="subtitle2" gutterBottom>
                  Advanced Options (Optional)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Strike Price Override"
                      placeholder="e.g., 24500"
                      value={strikeOverride}
                      onChange={(e) => setStrikeOverride(e.target.value)}
                      type="number"
                      helperText="Override auto-calculated strike price"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Quantity Multiplier"
                      placeholder="e.g., 2"
                      value={qtyMultiplier}
                      onChange={(e) => setQtyMultiplier(e.target.value)}
                      type="number"
                      inputProps={{ min: 0.1, max: 10, step: 0.1 }}
                      helperText="Multiply default quantity (1 = no change)"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Submit Button */}
            <Box mt={3} display="flex" justifyContent="center">
              <Button
                variant="contained"
                className={
                  orderType === 'EXIT'
                    ? classes.warningButton
                    : orderType === 'BEARISH ENTRY'
                      ? classes.bearishButton
                      : classes.submitButton
                }
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                onClick={handleSubmitOrder}
                disabled={submitting || !selectedCatalog || selectedUsers.length === 0}
                size="large"
              >
                {submitting ? 'Placing Orders...' : 'Place Order'}
              </Button>
            </Box>
          </Card>

          {/* Order Result */}
          {orderResult && (
            <Card className={`${classes.resultCard} ${orderResult.code === 'MO200' ? classes.successResult : classes.errorResult}`}>
              <Typography variant="h6" gutterBottom>
                Order Result
              </Typography>
              <Typography variant="body1">
                <strong>Status:</strong> {orderResult.code === 'MO200' ? 'Success' : 'Error'}
              </Typography>
              <Typography variant="body1">
                <strong>Message:</strong> {orderResult.message}
              </Typography>
              {orderResult.data && (
                <Box mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Users Processed:</strong> {orderResult.data.users_count || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Orders Initiated:</strong> {orderResult.data.orders_count || 0}
                  </Typography>
                </Box>
              )}
            </Card>
          )}
        </Grid>

        {/* Order Summary Sidebar */}
        <Grid item xs={12} md={4}>
          <Paper className={classes.summaryCard}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Order Summary
            </Typography>

            <Box className={classes.summaryRow}>
              <Typography className={classes.summaryLabel}>Catalog:</Typography>
              <Typography className={classes.summaryValue}>
                {selectedCatalogObj?.name || '-'}
              </Typography>
            </Box>

            <Box className={classes.summaryRow}>
              <Typography className={classes.summaryLabel}>Users:</Typography>
              <Typography className={classes.summaryValue}>
                {selectedUsers.length || '-'}
              </Typography>
            </Box>

            <Box className={classes.summaryRow}>
              <Typography className={classes.summaryLabel}>Symbol:</Typography>
              <Typography className={classes.summaryValue}>
                {selectedSymbol || '-'}
              </Typography>
            </Box>

            <Box className={classes.summaryRow}>
              <Typography className={classes.summaryLabel}>Order Type:</Typography>
              <Chip
                label={orderType}
                size="small"
                style={{
                  backgroundColor: orderTypeColors[orderType],
                  color: orderType === 'EXIT' ? '#000' : '#fff',
                }}
              />
            </Box>

            {showOverrides && (strikeOverride || (qtyMultiplier && qtyMultiplier !== '1')) && (
              <>
                <Divider className={classes.divider} />
                <Typography variant="subtitle2" gutterBottom>Overrides</Typography>
                {strikeOverride && (
                  <Box className={classes.summaryRow}>
                    <Typography className={classes.summaryLabel}>Strike:</Typography>
                    <Typography className={classes.summaryValue}>{strikeOverride}</Typography>
                  </Box>
                )}
                {qtyMultiplier && qtyMultiplier !== '1' && (
                  <Box className={classes.summaryRow}>
                    <Typography className={classes.summaryLabel}>Qty Multiplier:</Typography>
                    <Typography className={classes.summaryValue}>{qtyMultiplier}x</Typography>
                  </Box>
                )}
              </>
            )}

            <Divider className={classes.divider} />

            <Alert severity="info" style={{ marginTop: 8 }}>
              <Typography variant="caption">
                Orders will be placed for all selected users using their respective broker credentials.
              </Typography>
            </Alert>
          </Paper>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default PlaceOrder;
